import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import fundApi, { AUTH_TOKEN_KEY, setAuthToken, clearAuthToken } from '../api/fundApi';

const AuthContext = createContext(null);

/**
 * 登录态机：
 *   idle        初始
 *   need_change 后端要求先改密（持有 changeToken，未拿到正式 token）
 *   authed      已登录
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingChange, setPendingChange] = useState(null); // { changeToken, user } 改密握手态

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setAuthToken(null);
      setUser(null);
      setLoading(false);
      return;
    }
    setAuthToken(token);
    try {
      const res = await fundApi.getMe();
      if (res && res.success && res.data && res.data.user) {
        setUser(res.data.user);
      } else {
        setUser(null);
        clearAuthToken();
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    } catch {
      setUser(null);
      clearAuthToken();
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * 普通登录：
   *   - 成功 → 完成
   *   - 需改密 → 抛出 { code: 'PASSWORD_CHANGE_REQUIRED', changeToken, user, message }
   *     由调用方（Login 页）跳转 /change-password 走改密流程
   */
  const login = useCallback(async (username, password) => {
    const res = await fundApi.login(username, password);
    if (!res || !res.success) {
      throw new Error(res?.error || '登录失败');
    }
    // 后端在"需改密"分支仍返回 success=true，data={ require_password_change, change_token, user, message }
    if (res.data && res.data.require_password_change) {
      setPendingChange({
        changeToken: res.data.change_token,
        user: res.data.user,
        message: res.data.message,
      });
      const err = new Error(res.data.message || '请先修改密码');
      err.code = 'PASSWORD_CHANGE_REQUIRED';
      err.changeToken = res.data.change_token;
      err.user = res.data.user;
      throw err;
    }
    const token = res.data.token;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    setAuthToken(token);
    setUser(res.data.user);
    setPendingChange(null);
    return res.data;
  }, []);

  /**
   * 用 change_token 提交新密码。
   * 后端返回 { token, user }，写入会话后置 authed 态。
   */
  const submitPasswordChange = useCallback(async (newPassword) => {
    if (!pendingChange || !pendingChange.changeToken) {
      throw new Error('改密握手已过期，请重新登录');
    }
    const res = await fundApi.changePassword(pendingChange.changeToken, newPassword);
    if (!res || !res.success) {
      throw new Error(res?.error || '修改密码失败');
    }
    const token = res.data.token;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    setAuthToken(token);
    setUser(res.data.user);
    setPendingChange(null);
    return res.data;
  }, [pendingChange]);

  /**
   * 放弃改密，撤销 pendingChange 状态
   */
  const cancelPasswordChange = useCallback(() => {
    setPendingChange(null);
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
    setPendingChange(null);
    window.location.href = '/admin/login';
  }, []);

  const hasPermission = useCallback((code) => {
    if (!code) return true;
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    const perms = user.permissions;
    return Array.isArray(perms) && perms.includes(code);
  }, [user]);

  const hasAnyPermission = useCallback((codes) => {
    if (!codes) return true;
    const list = Array.isArray(codes) ? codes : [codes];
    if (!list.length) return true;
    return list.some((c) => hasPermission(c));
  }, [hasPermission]);

  const value = {
    user,
    loading,
    pendingChange,
    login,
    logout,
    loadUser,
    submitPasswordChange,
    cancelPasswordChange,
    hasPermission,
    hasAnyPermission,
    isSystemAdmin: user && user.role === 'system_admin',
    isAdmin: user && (user.role === 'system_admin' || user.role === 'admin'),
    canManageUsers: !!(user && (user.role === 'system_admin' || user.role === 'admin')),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}